<template>
    <benefits-modal
        class="reward-modal"
        :open-when="!!rewardId"
        @open="onOpen"
        @request-close="close"
    >
        <template slot="header">
            <header class="reward-detail-header" v-if="!shouldHideDetail">
                <template v-if="isLogged">
                    <span class="reward-detail-points-label" v-lang="'dashboard-yourPoints'"></span>
                    <span class="reward-detail-points-value">{{ balance }}</span>
                </template>
                <span class="reward-detail-points-label" v-else v-lang="'dashboard-loginToSeePoints'"></span>
            </header>
        </template>

        <redeem-confirm
            v-if="reward"
            :reward="reward"
            :balance="balance"
            @redeemed="onRedeemed"
        />

        <loader v-if="loading" />

        <div class="reward-detail" v-else-if="reward && !shouldHideDetail">
            <div class="reward-detail-image" :class="{ 'is-muted': isOutOfReach }" v-if="hasImage">
                <img :src="reward.image.url" :alt="reward.name" />
            </div>

            <div class="reward-detail-tags">
                <span class="reward-detail-tag" :class="canAfford ? 'is-affordable' : 'is-locked'">
                    {{ t("dashboard-rewardPoints", { points: reward.points }) }}
                </span>
                <span class="reward-detail-missing" v-if="missingLabel">{{ missingLabel }}</span>
            </div>

            <div class="reward-detail-tiers" v-if="isLocked && rewardTiers.length">
                <span
                    class="reward-detail-tiers-label"
                    v-lang="rewardTiers.length > 1 ? 'dashboard-rewardTierExclusiveMany' : 'dashboard-rewardTierExclusiveOne'"
                ></span>
                <span class="reward-detail-tiers-list">
                    <span
                        class="reward-detail-tier-chip"
                        v-for="tier in rewardTiers"
                        :key="tier.id"
                        :style="{ background: tier.color, color: tier.textColor }"
                    >
                        <tier-icon :tier="tier" />
                        {{ tier.name }}
                    </span>
                </span>
            </div>

            <h1 class="reward-detail-title">{{ reward.name }}</h1>
            <p class="reward-detail-subtitle" v-if="reward.subtitle">{{ reward.subtitle }}</p>
            <rich-text class="reward-detail-description" v-if="reward.description" :content="reward.description" />

            <div class="reward-detail-usage" v-if="reward.usageInstructions">
                <button
                    type="button"
                    class="reward-detail-usage-toggle"
                    :aria-expanded="usageOpen ? 'true' : 'false'"
                    @click="usageOpen = !usageOpen"
                >
                    <span v-lang="'dashboard-howToUse'"></span>
                    <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="#6c737f"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        :class="{ 'is-open': usageOpen }"
                        aria-hidden="true"
                    >
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>

                <p class="reward-detail-usage-body" v-if="usageOpen">{{ reward.usageInstructions }}</p>
            </div>
        </div>

        <template slot="footer">
            <div class="reward-detail-actions" v-if="reward && !shouldHideDetail">
                <button type="button" class="reward-detail-button" @click="close">
                    <span v-lang="'dashboard-back'"></span>
                </button>

                <button
                    v-if="!isLogged"
                    type="button"
                    class="reward-detail-button reward-detail-button--primary"
                    @click="goToLogin"
                    v-lang="'dashboard-loginToRedeem'"
                ></button>

                <button
                    v-else
                    type="button"
                    class="reward-detail-button"
                    :class="canAfford ? 'reward-detail-button--primary' : 'reward-detail-button--disabled'"
                    :disabled="!canAfford"
                    @click="openModal('redeemConfirm')"
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span v-lang="redeemButtonTerm"></span>
                </button>
            </div>
        </template>
    </benefits-modal>
</template>

<script>
import { mapState, mapMutations } from "vuex";

require("./points.store");
import loader from "../../shared/loader";
import BenefitsModal from "../../shared/modal-shell";
import RedeemConfirm from "./points-redeem-confirm";
import RichText from "../../shared/rich-text";
import TierIcon from "../../shared/tier-icon";

function hasTextContent(value) {
    if (!value) {
        return false;
    }

    const stripped = String(value)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

    return stripped.length > 0;
}

export default {
    name: "RewardModal",
    components: { loader, BenefitsModal, RedeemConfirm, RichText, TierIcon },
    data() {
        return {
            usageOpen: false,
        };
    },
    computed: {
        ...mapState({
            place: (state) => state.place,
            client: (state) => state.client,
            token: (state) => state.token,
            rewardId: (state) => state.rewardId,
            balance: (state) => state.dashboard.balance,
            rewards: (state) => state.dashboard.rewards,
            tiers: (state) => state.dashboard.tiers,
            loading: (state) => state.dashboard.loading,
        }),
        isLogged() {
            return !!(this.client && this.client.id && this.token);
        },
        reward() {
            return this.rewards.filter((item) => String(item.id) === this.rewardId && item.access !== "blurred")[0] || null;
        },
        isLocked() {
            return !!(this.reward && this.reward.access === "locked");
        },
        rewardTiers() {
            const ids = (this.reward && this.reward.tierIds) || [];

            return this.tiers
                .filter((tier) => ids.includes(tier.id))
                .sort((a, b) => a.position - b.position);
        },
        redeemButtonTerm() {
            if (this.isLocked) {
                return "dashboard-redeemTierLocked";
            }

            return this.canAfford ? "dashboard-redeemReward" : "dashboard-insufficientPoints";
        },
        hasImage() {
            return !!(this.reward && this.reward.image && this.reward.image.url);
        },
        canAfford() {
            return this.isLogged && !!this.reward && !this.isLocked && this.balance >= this.reward.points;
        },
        isOutOfReach() {
            return !this.canAfford;
        },
        showAsUnreachable() {
            return this.isLogged && !this.isLocked && !this.canAfford;
        },
        missingLabel() {
            if (!this.showAsUnreachable || !this.reward) {
                return "";
            }

            const missing = this.reward.points - this.balance;

            return missing === 1
                ? this.t("dashboard-missingOnePoint")
                : this.t("dashboard-missingPoints", { points: missing });
        },
        hasDetailContent() {
            if (!this.reward) {
                return false;
            }

            if (this.hasImage) {
                return true;
            }

            return (
                hasTextContent(this.reward.subtitle) ||
                hasTextContent(this.reward.description) ||
                hasTextContent(this.reward.usageInstructions)
            );
        },
        shouldSkipToConfirm() {
            return !!(this.reward && this.isLogged && this.canAfford && !this.hasDetailContent);
        },
        showRedeemConfirm() {
            return this.$store.state.modal === "redeemConfirm";
        },
        shouldHideDetail() {
            return this.showRedeemConfirm || this.shouldSkipToConfirm;
        },
    },
    watch: {
        reward(value) {
            if (this.rewardId && !this.loading && !value) {
                this.close();
                return;
            }

            this.maybeOpenRedeemConfirm();
        },
        rewardId(value) {
            if (value) {
                this.maybeOpenRedeemConfirm();
            }
        },
        "$store.state.modal"(modal, previous) {
            if (previous === "redeemConfirm" && !modal && this.shouldSkipToConfirm) {
                this.closeReward();
            }
        },
    },
    methods: {
        ...mapMutations(["openModal", "closeModal", "closeReward", "openRedeemSuccess"]),
        onOpen() {
            this.usageOpen = false;

            if (!this.rewards.length && this.place.id) {
                this.$store.dispatch("dashboard/loadData");
            }

            this.maybeOpenRedeemConfirm();
        },
        maybeOpenRedeemConfirm() {
            if (this.shouldSkipToConfirm && !this.showRedeemConfirm) {
                this.openModal("redeemConfirm");
            }
        },
        onRedeemed(result) {
            this.openRedeemSuccess({
                reward: result.reward,
                message: result.message || this.t("dashboard-redeemSuccessMessage"),
            });

            this.$store.dispatch("dashboard/loadData");
            this.closeAll();
        },
        close() {
            if (this.$store.state.modal === "redeemConfirm") {
                if (this.shouldSkipToConfirm) {
                    this.closeAll();
                    return;
                }

                this.closeModal();
                return;
            }

            this.closeReward();
        },
        closeAll() {
            this.closeModal();
            this.closeReward();
        },
        goToLogin() {
            this.close();
            this.$router.push("/");
        },
    },
};
</script>

<style lang="scss">
.reward-detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 30rem;
  margin: 0 auto;
  padding: 1.5rem 1rem;
}

.reward-detail-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-height: 5.875rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-bottom: 1px solid #d2d6db;
  text-align: center;
}

.reward-detail-points-label {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.3;
  color: #4d5761;
}

.reward-detail-points-value {
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.3;
  color: #17b26a;
}

.reward-detail-image {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;

  @supports not (aspect-ratio: 1) {
    height: 21.4375rem;
  }
}

.reward-detail-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.reward-detail-image.is-muted img {
  filter: grayscale(1);
}

.reward-detail-tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.reward-detail-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.3;
}

.reward-detail-tag.is-affordable {
  background: #d3f8e0;
  color: #0b9055;
}

.reward-detail-tag.is-locked {
  background: #e5e7eb;
  color: #4d5761;
}

.reward-detail-tiers {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reward-detail-tiers-label {
  font-size: 0.875rem;
  line-height: 1.3;
  color: #4d5761;
}

.reward-detail-tiers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.reward-detail-tier-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
}

.reward-detail-missing {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: #384250;
}

.reward-detail-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.3;
  color: #4d5761;
  overflow-wrap: anywhere;
}

.reward-detail-subtitle {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: #4d5761;
  overflow-wrap: anywhere;
}

.reward-detail-description {
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.6;
  color: #6c727e;
}

.reward-detail-usage {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.reward-detail-usage-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.1875rem;
  width: 100%;
  padding: 0.25rem 0.5rem;
  min-height: 2rem;
  border: none;
  border-radius: 0.5rem;
  background: #f3f4f6;
  color: #111927;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
}

.reward-detail-usage-toggle svg {
  flex: 0 0 auto;
  transition: transform 0.2s ease;
}

.reward-detail-usage-toggle svg.is-open {
  transform: rotate(180deg);
}

.reward-detail-usage-body {
  margin: 0;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #4d5761;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.reward-detail-actions {
  flex: 1 1 auto;
  display: flex;
  gap: 1rem;
}

.reward-detail-button {
  flex: 1 1 0;
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fcfcfd;
  color: #1f2a37;
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.98);
  }
}

.reward-detail-button--primary {
  flex: 1.5 1 0;
  border-color: transparent;
  background: #2aaa4c;
  color: #f1fcf3;
}

.reward-detail-button--disabled {
  flex: 1.5 1 0;
  border-color: transparent;
  background: #e5e7eb;
  color: #6c727e;
  cursor: not-allowed;

  &:active {
    transform: none;
  }
}

@media (min-width: 48rem) {
  .reward-modal .benefits-modal-panel {
    height: 53.4375rem;
    border-radius: 1rem;
  }

  .reward-modal .reward-detail-header {
    position: sticky;
    top: 0;
    z-index: 10;
    min-height: 0;
    padding: 1.5rem 1.5rem 0;
    background: #ffffff;
    border-bottom: none;
  }

  .reward-modal .reward-detail {
    max-width: none;
    padding: 1rem 1.5rem;
  }

  .reward-modal .reward-detail-image {
    aspect-ratio: 852 / 430;

    @supports not (aspect-ratio: 852 / 430) {
      height: 26.875rem;
    }
  }

  .reward-modal .benefits-modal-footer {
    padding: 1rem 1.5rem 1.5rem;
    background: #ffffff;
    border-top: none;
  }

  .reward-modal .reward-detail-actions {
    justify-content: space-between;
  }

  .reward-modal .reward-detail-button {
    flex: 0 0 auto;
  }
}
</style>
